/**
 * The types, constants and copy strings the league row and league menu use.
 *
 * Mirrors ISSUE-074's mobile contract (`apps/mobile/src/scores/leagueSelector/
 * leagueSelectorContract.ts`, not yet built) so web and mobile say the same
 * words for the same control. Named the pinned-league-row plus league-menu
 * decisions the product owner made, batch H, questions 276 and 277.
 */

/** One league as the selector sees it. `leagueId` is the catalog UUID and is
 *  the value stored on the profile. `leagueKey` is the kebab key. Never
 *  derive one from the other. */
export type typeLeagueSelectorOption = {
  leagueId: string;
  leagueKey: string;
  name: string;
  shortName: string;
  isComingSoon: boolean;
};

/** The horizontal row. `all` is always index 0 and is never a league.
 *  `pinned` holds the fan's pinned leagues and the leagues of their favorite
 *  teams, and ends with the league being viewed when that league is neither
 *  (ISSUE-949), so a viewed league the catalog resolves always has a chip to
 *  mark. An `activeLeagueId` the catalog does not resolve gets no chip of its
 *  own, and `all.isActive` is false because a league is active, so the row
 *  then marks nothing. */
export type typeLeagueSelectorRow = {
  all: { label: string; isActive: boolean };
  pinned: (typeLeagueSelectorOption & { isActive: boolean })[];
};

/** One sport group in the menu. A heading only. Nothing here is selectable. */
export type typeLeagueMenuSport = {
  sportId: string;
  sportKey: string;
  heading: string;
  leagues: (typeLeagueSelectorOption & { isPinned: boolean })[];
};

export type typeLeagueMenuPhase = "loading" | "error" | "empty" | "ready";

export type typeLeagueMenuView = {
  phase: typeLeagueMenuPhase;
  /** The fan's pinned leagues, in the fan's own stored order. */
  pinned: typeLeagueSelectorOption[];
  /** Catalog sports in catalog order, each holding only the leagues this fan
   *  has NOT pinned. A sport left with nothing is absent. Disjoint from
   *  `pinned` on purpose: ISSUE-907, a league listed in both printed its name
   *  twice in one sheet. */
  sports: typeLeagueMenuSport[];
};

export type typeMoveDirection = "up" | "down";

// Mirrored from ISSUE-074's contract so web and mobile say the same words.
export const ALL_LEAGUES_LABEL = "All";
export const ALL_LEAGUES_KEY = "all";
export const LEAGUE_MENU_TITLE = "Your leagues";
export const LEAGUE_MENU_PINNED_HEADING = "Pinned";
export const LEAGUE_MENU_OPEN_LABEL = "Choose leagues";
export const COMING_SOON_LABEL = "Coming soon";
export const NO_PINS_LINE =
  "Nothing pinned yet. Pin a league and it shows up at the top of Scores.";
export const NO_LEAGUES_TITLE = "No leagues yet";
export const NO_LEAGUES_BODY =
  "We are still setting up the league list. Check back soon.";
export const MAX_PINNED_LEAGUES = 100;

// Web-only, taken from design-prompts/12-scores.md and the rendered frames.
export const FAVORITE_TEAMS_TITLE = "Your teams";
export const COLD_START_TITLE = "Pick the leagues you follow";
export const COLD_START_BODY =
  "Choose the leagues you follow and we'll put their games first.";
export const NO_GAMES_TITLE = "No games today";
/** The second line under "Coming soon" on a league the catalog marks
 *  comingSoon (ISSUE-1020, SCORES-3). Fan-facing: the app is already live,
 *  so it says "soon", never "after launch". */
export const LEAGUE_COMING_SOON_BODY =
  "This league's scores aren't ready yet. Check back soon.";
export const JUMP_TO_TODAY_LABEL = "Jump to today";
export const SCORES_LOADING_LABEL = "Loading scores";
export const SCORES_FAILED_LINE = "We could not load these scores.";
export const TRY_AGAIN_LABEL = "Try again";
export const LEAGUE_MENU_TRIGGER_LABEL = "Open the league menu";
export const PREVIOUS_MONTH_LABEL = "Previous month";
export const NEXT_MONTH_LABEL = "Next month";
export const DAY_STRIP_LABEL = "Day";
/** The week strip's accessible name on a football league. The calendar it
 *  sits beside still opens `CALENDAR_DIALOG_TITLE`: picking a day is the
 *  same action there, it just selects that day's week (ISSUE-950 R9). */
export const WEEK_STRIP_LABEL = "Week";
export const CALENDAR_DIALOG_TITLE = "Pick a day";

// Scores UX spec v1.0 (2026-10-20). Mirrored on mobile in
// `apps/mobile/src/scores/leagueSelector/leagueSelectorContract.ts`.
/** The visible label of the chip row's manage control; its accessible name is
 *  `LEAGUE_MENU_OPEN_LABEL`. */
export const LEAGUE_MENU_MANAGE_LABEL = "Leagues";
/** The chip row's accessible name. */
export const LEAGUE_CHIPS_LABEL = "Leagues";
export const SEE_ALL_LABEL = "See all";
export const STANDINGS_LINK_LABEL = "Standings";
/** "See all NFL games", the See all link's accessible name. */
export function seeAllLabel(leagueName: string): string {
  return `${SEE_ALL_LABEL} ${leagueName} games`;
}
/** "2 live", beside a section title while games are in play. */
export function liveCountLabel(count: number): string {
  return `${String(count)} live`;
}
/** "No games on Monday, Oct 19", inside the league view's card. */
export function noGamesOnLabel(dayLabel: string): string {
  return `No games on ${dayLabel}`;
}
/** "Go to Thu, Oct 22", the empty day's jump to the next game day. */
export function goToDayLabel(shortDay: string): string {
  return `Go to ${shortDay}`;
}

/** ISSUE-977. What a screen reader reads after a pinned row's grip handle name. */
export const REORDER_HANDLE_INSTRUCTIONS =
  "Drag to change the order, or press the up or down arrow key to move this league one place.";
/** ISSUE-977. The grip handle's accessible name. */
export function reorderHandleLabel(shortName: string): string {
  return `Reorder ${shortName}`;
}
/** ISSUE-977. Read by the live region after a move. */
export function reorderMovedAnnouncement(input: {
  shortName: string;
  position: number;
  total: number;
}): string {
  return `${input.shortName} moved to position ${String(input.position)} of ${String(input.total)}.`;
}
/** ISSUE-977. Read by the live region when an arrow key finds the league
 *  already at that end. */
export function reorderAtEdgeAnnouncement(input: {
  shortName: string;
  position: number;
  total: number;
}): string {
  return `${input.shortName} is already at position ${String(input.position)} of ${String(input.total)}.`;
}
/** ISSUE-977. dnd-kit's built-in drag announcements read raw league ids, so
 *  every one of them says nothing; the sheet's own polite live region
 *  speaks instead. */
export function silentDragAnnouncement(): undefined {
  return undefined;
}

/** The house month abbreviations ("Sept", never "Sep"). One table, in
 *  `@sportsmet/theme/scores`, shared with mobile. */
export { MONTH_SHORT as MONTH_ABBREVIATIONS } from "@sportsmet/theme/scores";
